import {
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  OdinIcon,
  type OdinIconName,
  OdinIconSize,
  Tooltip,
} from '@myosh/odin-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetRecordsInsightsResultsQuery,
  useGetWidgetsDataAnalysisResultQuery,
  useStartRecordsInsightsAnalysisMutation,
  useStartWidgetsDataAnalysisMutation,
} from '../../redux/config/widgetApi';
import { useAppDispatch } from '../../redux/hooks';
import {
  deleteSidePanelItem,
  JobStatus,
  type SidePanelItem,
  setActiveSidePanelItemKey,
  setSidePanelItemJob,
} from '../../redux/slices/side-panel-slice';

interface SidePanelNavItemComponentProps {
  item: SidePanelItem;
  className?: string;
  displayCollapsed: boolean;
}

function SidePanelNavItemComponent({ item, className, displayCollapsed }: SidePanelNavItemComponentProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isRecordInsight = item.type === 'RECORD_INSIGHT';
  const jobId = item.job?.id;

  const [startRecordsInsightsAnalysisJob] = useStartRecordsInsightsAnalysisMutation();
  const [startWidgetsDataAnalysisJob] = useStartWidgetsDataAnalysisMutation();

  const { currentData: aiAnalysisData, isError: isAiAnalysisError } = useGetWidgetsDataAnalysisResultQuery(
    { analysisId: jobId! },
    {
      skip: !jobId || isRecordInsight,
      pollingInterval: jobId ? 5000 : 0,
    }
  );

  const { currentData: recordsInsightData, isError: isRecordsInsightError } = useGetRecordsInsightsResultsQuery(
    { analysisId: jobId! },
    {
      skip: !jobId || !isRecordInsight,
      pollingInterval: jobId ? 5000 : 0,
    }
  );

  const currentResponse = isRecordInsight ? recordsInsightData : aiAnalysisData;
  const result = currentResponse?.result;
  const status = currentResponse?.status;
  const error = currentResponse?.error;
  const isError = isRecordInsight ? isRecordsInsightError : isAiAnalysisError;

  const generalErrorMessage = t(
    'generateInsightFailed',
    'We couldn’t complete this request. Please try again. If the problem continues, contact support'
  );

  const displayText = item.title;

  const RecordInfo = () => <div className="flex-1 truncate text-xs min-w-0">{displayText}</div>;

  const onNavItemClicked = (key: string) => {
    dispatch(setActiveSidePanelItemKey(key));
  };

  const onNavItemCloseClicked = (key: string) => {
    dispatch(deleteSidePanelItem(key));
  };

  const submitRecordInsightJob = async () => {
    const jobStartedDate = new Date().toISOString();

    try {
      dispatch(
        setSidePanelItemJob({ key: item.key, job: { startedDate: jobStartedDate, status: JobStatus.InProgress } })
      );

      let id: string | undefined;

      if (isRecordInsight) {
        const payload = { widgetId: item.widgetId };
        id = (await startRecordsInsightsAnalysisJob(payload).unwrap()) ?? undefined;
      } else {
        const payload = {
          widgetId: item.widgetId,
          widgetInsightsDataDto: {
            extraPrompt: item.job?.extraPrompt,
            data: item.chartData,
          },
        };
        id = (await startWidgetsDataAnalysisJob(payload).unwrap()) ?? undefined;
      }

      if (id) {
        dispatch(setSidePanelItemJob({ key: item.key, job: { id: id } }));
      } else {
        dispatch(
          setSidePanelItemJob({
            key: item.key,
            job: {
              id: undefined,
              error: generalErrorMessage,
              status: JobStatus.Failed,
              completedDate: new Date().toISOString(),
            },
          })
        );
      }
    } catch (error) {
      const errorMessage = (error as Record<string, unknown> | null)?.data;
      const errorMessageText = typeof errorMessage === 'string' ? errorMessage : generalErrorMessage;

      dispatch(
        setSidePanelItemJob({
          key: item.key,
          job: {
            id: undefined,
            error: errorMessageText,
            status: JobStatus.Failed,
            completedDate: new Date().toISOString(),
          },
        })
      );
    }
  };

  useEffect(() => {
    if (item.job?.status === undefined && item.job?.id === undefined) {
      submitRecordInsightJob();
    }
  }, [item.job?.status, item.job?.id]);

  useEffect(() => {
    if (jobId) {
      if ('FINISHED' === status && result) {
        dispatch(
          setSidePanelItemJob({
            key: item.key,
            job: {
              id: undefined,
              error: undefined,
              status: JobStatus.Success,
              completedDate: new Date().toISOString(),
              result: result,
            },
          })
        );
      } else if ('FAILED' === status || isError) {
        dispatch(
          setSidePanelItemJob({
            key: item.key,
            job: {
              id: undefined,
              error: generalErrorMessage,
              status: JobStatus.Failed,
              completedDate: new Date().toISOString(),
            },
          })
        );
      }
    }
  }, [status, isError, jobId]);

  return (
    <li key={`panel_item_${item.key}`} className={className} onClick={() => onNavItemClicked(item.key)}>
      <a className="flex items-center justify-start">
        <Tooltip
          arrow={true}
          description={<RecordInfo />}
          tooltipAlignment={DomElementAlignment.TopRight}
          tooltipTargetPosition={DomTargetPosition.Left}
          tooltipClassName={'h-10 flex justify-start items-center bg-gray-1 text-mono-0'}
          disabled={!displayCollapsed}
        >
          <OdinIcon
            className="text-inherit m-3 shrink"
            size={OdinIconSize.ExtraSmall}
            icon={(item.icon as OdinIconName) || 'ClipboardList'}
          />
        </Tooltip>
        {!displayCollapsed && (
          <>
            <RecordInfo />
            <IconButton
              icon="X"
              iconSize={OdinIconSize.ExtraSmall}
              iconClassName="relative -left-2.5"
              classNames="ml-4"
              onClick={(event) => {
                onNavItemCloseClicked(item.key);
                event.stopPropagation();
              }}
            />
          </>
        )}
      </a>
    </li>
  );
}

export default SidePanelNavItemComponent;
